import React, { useState } from 'react';
import { CheckCircle2Icon, ClockIcon, FileTextIcon, PlusIcon, AlertTriangleIcon, MicIcon, ClipboardListIcon, ChevronRightIcon, ArrowLeftIcon, FlagIcon, BadgeCheckIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { ScreenProps } from '../../types/ptp';
import { PrimaryButton } from '../ui/PrimaryButton';
import { SecondaryButton } from '../ui/SecondaryButton';
import { DashboardScreen } from './DashboardScreen';
import { PostJobReviewScreen, PostJobReport } from './PostJobReviewScreen';
import { useI18n } from '../../config/i18n';
export interface PTPListItem {
  id: string;
  task: string;
  location: string;
  contractor: string;
  date: string;
  status: 'draft' | 'ready' | 'signed' | 'job-done';
  score?: number;
  crewSize: number;
  meetingDone?: boolean;
  quizDone?: boolean;
  taskCompleted?: boolean;
}
// Helper to format dates
function formatDate(d: Date): string {
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}
function getDateLabel(dateStr: string): string {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const todayStr = formatDate(today);
  const yesterdayStr = formatDate(yesterday);
  if (dateStr === todayStr) return '@@today';
  if (dateStr === yesterdayStr) return '@@yesterday';
  return dateStr;
}
const TODAY = formatDate(new Date());
const YESTERDAY = formatDate((() => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d;
})());
const TWO_DAYS_AGO = formatDate((() => {
  const d = new Date();
  d.setDate(d.getDate() - 2);
  return d;
})());
const PTP_LIST: PTPListItem[] = [
// Today — item 0 is the "current" PTP populated from user data
{
  id: 'PTP-2026-00851',
  task: '',
  location: '',
  contractor: 'Apex Steel Works',
  date: TODAY,
  status: 'draft',
  crewSize: 0,
  meetingDone: false,
  quizDone: false,
  taskCompleted: false
}, {
  id: 'PTP-2026-00850',
  task: 'Foundation Pour, East Wing',
  location: 'Ground Level East',
  contractor: 'Pinnacle Concrete Co.',
  date: TODAY,
  status: 'signed',
  score: 91,
  crewSize: 12,
  meetingDone: true,
  quizDone: true,
  taskCompleted: false
}, {
  id: 'PTP-2026-00849',
  task: 'Steel Beam Lift, Tower B',
  location: 'Levels 4–6',
  contractor: 'Ironside Erectors',
  date: TODAY,
  status: 'ready',
  crewSize: 6,
  meetingDone: false,
  quizDone: false,
  taskCompleted: false
},
// Yesterday
{
  id: 'PTP-2026-00848',
  task: 'Scaffold Build, North Face',
  location: 'North Exterior',
  contractor: 'Vertex Access Solutions',
  date: YESTERDAY,
  status: 'job-done',
  score: 88,
  crewSize: 5,
  meetingDone: true,
  quizDone: true,
  taskCompleted: true
}, {
  id: 'PTP-2026-00847',
  task: 'Fire Suppression Install, L3',
  location: 'Level 3 Mechanical',
  contractor: 'RedLine Fire Protection',
  date: YESTERDAY,
  status: 'signed',
  score: 85,
  crewSize: 4,
  meetingDone: true,
  quizDone: true,
  taskCompleted: false
}, {
  id: 'PTP-2026-00846',
  task: 'Roof Membrane Application',
  location: 'Roof Level',
  contractor: 'AllWeather Roofing',
  date: YESTERDAY,
  status: 'ready',
  score: 72,
  crewSize: 7,
  meetingDone: true,
  quizDone: false,
  taskCompleted: false
},
// Older
{
  id: 'PTP-2026-00845',
  task: 'Panel Wiring, Main Switchgear',
  location: 'Electrical Room B1',
  contractor: 'Conduit Electric',
  date: TWO_DAYS_AGO,
  status: 'job-done',
  score: 94,
  crewSize: 3,
  meetingDone: true,
  quizDone: true,
  taskCompleted: true
}];
export function Screen21_PTPList({
  data,
  onNext,
  onCardPress,
  showNewPTP = false
}: ScreenProps & {
  showNewPTP?: boolean;
  onCardPress?: (ptp: PTPListItem) => void;
}) {
  const {
    t
  } = useI18n();
  const statusConfig: Record<string, {
    label: string;
    icon: typeof CheckCircle2Icon;
    iconColor: string;
    bg: string;
    text: string;
  }> = {
    draft: {
      label: t('status.draft'),
      icon: FileTextIcon,
      iconColor: 'text-gray-400',
      bg: 'bg-gray-100',
      text: 'text-gray-500'
    },
    ready: {
      label: t('status.ready'),
      icon: CheckCircle2Icon,
      iconColor: 'text-amber-600',
      bg: 'bg-amber-50',
      text: 'text-amber-600'
    },
    signed: {
      label: t('status.signed'),
      icon: ClipboardListIcon,
      iconColor: 'text-green-600',
      bg: 'bg-green-50',
      text: 'text-green-600'
    },
    'job-done': {
      label: t('status.jobDone'),
      icon: BadgeCheckIcon,
      iconColor: 'text-blue-600',
      bg: 'bg-blue-50',
      text: 'text-blue-600'
    }
  };
  const [activeTab, setActiveTab] = useState<'plans' | 'dashboard'>('plans');
  const [statusFilter, setStatusFilter] = useState<'all' | 'draft' | 'ready' | 'signed' | 'job-done'>('all');
  const [selectedPTP, setSelectedPTP] = useState<PTPListItem | null>(null);
  const [showPostJob, setShowPostJob] = useState(false);
  const [ptpListState, setPtpListState] = useState(() => {
    const list = [...PTP_LIST];
    // Populate the first item from actual PTP data
    list[0] = {
      ...list[0],
      task: data.taskTitle || 'New Pre-Task Plan',
      location: data.location || 'Location TBD',
      date: data.date || new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }),
      crewSize: data.crewSize || data.crewRoles.length || 0
    };
    return list;
  });
  const visiblePTPs = showNewPTP ? [{
    ...ptpListState[0],
    task: data.taskTitle || ptpListState[0].task,
    location: data.location || ptpListState[0].location,
    crewSize: data.crewSize || data.crewRoles.length || ptpListState[0].crewSize,
    status: 'ready' as const,
    score: data.meetingScore || 72,
    meetingDone: false,
    quizDone: false,
    taskCompleted: false
  }, ...ptpListState.slice(1)] : ptpListState;
  const handleCardPress = (ptp: PTPListItem) => {
    setSelectedPTP(ptp);
  };
  const handleAction = (ptp: PTPListItem) => {
    if (onCardPress) {
      onCardPress(ptp);
    }
  };
  const handlePostJobSubmit = (report: PostJobReport) => {
    if (selectedPTP) {
      // Update the PTP status to job-done
      setPtpListState(prev => prev.map(p => p.id === selectedPTP.id ? {
        ...p,
        status: 'job-done' as const,
        taskCompleted: true
      } : p));
      setSelectedPTP({
        ...selectedPTP,
        status: 'job-done',
        taskCompleted: true
      });
      setShowPostJob(false);
    }
  };
  // Post-Job Review sub-view
  if (showPostJob && selectedPTP) {
    return <PostJobReviewScreen ptpTask={selectedPTP.task} ptpId={selectedPTP.id} onSubmit={handlePostJobSubmit} onCancel={() => setShowPostJob(false)} data-id="element-1425" />;
  }
  // Detail view for a selected PTP
  if (selectedPTP) {
    const config = statusConfig[selectedPTP.status];
    const StatusIcon = config.icon;
    const needsMeeting = !selectedPTP.meetingDone;
    const needsQuiz = selectedPTP.meetingDone && !selectedPTP.quizDone;
    const canMarkComplete = selectedPTP.meetingDone && selectedPTP.quizDone && !selectedPTP.taskCompleted;
    const isJobDone = selectedPTP.taskCompleted;
    return <div className="flex flex-col h-full" data-id="element-1426">
        <button onClick={() => setSelectedPTP(null)} className="flex items-center gap-1.5 text-gray-500 text-sm font-medium mb-4 -ml-1 self-start" data-id="element-1427">
          <ArrowLeftIcon className="w-4 h-4" data-id="element-1428" />
          {t('list.backToPlans')}
        </button>

        <div className="flex-1 overflow-y-auto pb-6 -mx-2 px-2 space-y-4" data-id="element-1429">
          {/* PTP Header */}
          <div className="bg-white border border-gray-200 rounded-xl p-5" data-id="element-1430">
            <div className="flex items-start justify-between gap-3 mb-3" data-id="element-1431">
              <div className={`${config.bg} px-2.5 py-1 rounded-full flex items-center gap-1.5`} data-id="element-1432">
                <StatusIcon className={`w-3.5 h-3.5 ${config.iconColor}`} data-id="element-1433" />
                <span className={`text-xs font-bold uppercase tracking-wider ${config.text}`} data-id="element-1434">
                  {config.label}
                </span>
              </div>
              <span className="text-[10px] text-gray-400" data-id="element-1435">
                {selectedPTP.id}
              </span>
            </div>

            <h3 className="text-gray-900 font-bold text-lg leading-snug mb-2" data-id="element-1436">
              {selectedPTP.task}
            </h3>

            <div className="space-y-2 text-sm" data-id="element-1437">
              <div className="flex items-center justify-between" data-id="element-1438">
                <span className="text-gray-400" data-id="element-1439">{t('newPtp.location')}</span>
                <span className="text-gray-700" data-id="element-1440">{selectedPTP.location}</span>
              </div>
              <div className="flex items-center justify-between" data-id="element-1441">
                <span className="text-gray-400" data-id="element-1442">{t('list.contractor')}</span>
                <span className="text-gray-700" data-id="element-1443">{selectedPTP.contractor}</span>
              </div>
              <div className="flex items-center justify-between" data-id="element-1444">
                <span className="text-gray-400" data-id="element-1445">{t('newPtp.date')}</span>
                <span className="text-gray-700" data-id="element-1446">{selectedPTP.date}</span>
              </div>
              <div className="flex items-center justify-between" data-id="element-1447">
                <span className="text-gray-400" data-id="element-1448">{t('crew.size')}</span>
                <span className="text-gray-700" data-id="element-1449">
                  {selectedPTP.crewSize} {t('list.workers')}
                </span>
              </div>
              {selectedPTP.score && <div className="flex items-center justify-between" data-id="element-1450">
                  <span className="text-gray-400" data-id="element-1451">
                    {t('list.meetingScore')}
                  </span>
                  <span className={`font-bold ${selectedPTP.score >= 80 ? 'text-green-600' : 'text-amber-600'}`} data-id="element-1452">
                    {selectedPTP.score}/100
                  </span>
                </div>}
            </div>
          </div>

          {/* Progress Checklist */}
          <div className="bg-white border border-gray-200 rounded-xl p-4" data-id="element-1453">
            <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3" data-id="element-1454">
              {t('list.ptpProgress')}
            </h4>
            <div className="space-y-3" data-id="element-1455">
              <div className="flex items-center gap-3" data-id="element-1456">
                <CheckCircle2Icon className="w-4 h-4 text-green-600" data-id="element-1457" />
                <span className="text-sm text-gray-700" data-id="element-1458">
                  {t('list.ptpCreated')}
                </span>
              </div>
              <div className="flex items-center gap-3" data-id="element-1459">
                {selectedPTP.meetingDone ? <CheckCircle2Icon className="w-4 h-4 text-green-600" data-id="element-1460" /> : <div className="w-4 h-4 rounded-full border-2 border-gray-300" data-id="element-1461" />}
                <span className={`text-sm ${selectedPTP.meetingDone ? 'text-gray-700' : 'text-gray-400'}`} data-id="element-1462">
                  {t('list.meetingRecorded')}
                </span>
              </div>
              <div className="flex items-center gap-3" data-id="element-1463">
                {selectedPTP.quizDone ? <CheckCircle2Icon className="w-4 h-4 text-green-600" data-id="element-1464" /> : <div className="w-4 h-4 rounded-full border-2 border-gray-300" data-id="element-1465" />}
                <span className={`text-sm ${selectedPTP.quizDone ? 'text-gray-700' : 'text-gray-400'}`} data-id="element-1466">
                  {t('list.quizCompleted')}
                </span>
              </div>
              <div className="flex items-center gap-3" data-id="element-1467">
                {selectedPTP.taskCompleted ? <CheckCircle2Icon className="w-4 h-4 text-blue-600" data-id="element-1468" /> : <div className="w-4 h-4 rounded-full border-2 border-gray-300" data-id="element-1469" />}
                <span className={`text-sm ${selectedPTP.taskCompleted ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-1470">
                  {t('list.taskCompleted')}
                </span>
              </div>
            </div>
          </div>

          {/* Status-specific messages */}
          {needsMeeting && <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-2.5" data-id="element-1471">
              <MicIcon className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-1472" />
              <p className="text-amber-700 text-xs leading-relaxed" data-id="element-1473">
                {t('list.needsMeetingMsg')}
              </p>
            </div>}
          {needsQuiz && <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 flex items-start gap-2.5" data-id="element-1474">
              <ClipboardListIcon className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-1475" />
              <p className="text-blue-700 text-xs leading-relaxed" data-id="element-1476">
                {t('list.needsQuizMsg')}
              </p>
            </div>}
          {canMarkComplete && <div className="bg-yellow-50 border border-yellow-200 rounded-xl px-4 py-3 flex items-start gap-2.5" data-id="element-1477">
              <FlagIcon className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" data-id="element-1478" />
              <p className="text-yellow-700 text-xs leading-relaxed" data-id="element-1479">
                {t('list.canCompleteMsg')}
              </p>
            </div>}
          {isJobDone && <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 flex items-start gap-2.5" data-id="element-1480">
              <BadgeCheckIcon className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-1481" />
              <p className="text-blue-700 text-xs leading-relaxed" data-id="element-1482">
                {t('list.jobDoneMsg')}
              </p>
            </div>}
        </div>

        <div className="sticky bottom-0 z-10 pt-3 pb-2 space-y-3 bg-gray-50" data-id="element-1483">
          {needsMeeting && <PrimaryButton onClick={() => handleAction(selectedPTP)} icon={<MicIcon className="w-5 h-5" data-id="element-1485" />} data-id="element-1484">
              {t('list.recordMeeting')}
            </PrimaryButton>}
          {needsQuiz && <PrimaryButton onClick={() => handleAction(selectedPTP)} icon={<ClipboardListIcon className="w-5 h-5" data-id="element-1487" />} data-id="element-1486">
              {t('list.sendQuiz')}
            </PrimaryButton>}
          {canMarkComplete && <PrimaryButton onClick={() => setShowPostJob(true)} icon={<FlagIcon className="w-5 h-5" data-id="element-1489" />} data-id="element-1488">
              {t('list.markComplete')}
            </PrimaryButton>}
          {isJobDone && <PrimaryButton onClick={() => handleAction(selectedPTP)} icon={<FileTextIcon className="w-5 h-5" data-id="element-1491" />} data-id="element-1490">
              {t('list.viewFullPtp')}
            </PrimaryButton>}
          <SecondaryButton onClick={() => setSelectedPTP(null)} data-id="element-1492">
            {t('list.backToPlans')}
          </SecondaryButton>
        </div>
      </div>;
  }
  // List view
  return <div className="flex flex-col h-full relative" data-id="element-1493">
      {data.project && <div className="mb-4" data-id="element-1494">
          <p className="text-gray-500 text-sm font-medium" data-id="element-1495">{data.project}</p>
        </div>}

      <div className="bg-gray-100 p-1 rounded-lg flex mb-5 flex-shrink-0" data-id="element-1496">
        <button onClick={() => setActiveTab('plans')} className={`flex-1 py-1.5 text-sm font-semibold rounded-md transition-colors ${activeTab === 'plans' ? 'bg-yellow-400 text-gray-900 shadow-sm' : 'text-gray-500'}`} data-id="element-1497">
          {t('list.plans')}
        </button>
        <button onClick={() => setActiveTab('dashboard')} className={`flex-1 py-1.5 text-sm font-semibold rounded-md transition-colors ${activeTab === 'dashboard' ? 'bg-yellow-400 text-gray-900 shadow-sm' : 'text-gray-500'}`} data-id="element-1498">
          {t('list.dashboard')}
        </button>
      </div>

      {activeTab === 'dashboard' ? <DashboardScreen data-id="element-1499" /> : <>
          {/* Quick Status Filters */}
          <div className="flex gap-2 mb-4 flex-shrink-0" data-id="element-1500">
            {([{
          key: 'all',
          label: t('status.all')
        }, {
          key: 'draft',
          label: t('status.draft')
        }, {
          key: 'ready',
          label: t('status.ready')
        }, {
          key: 'signed',
          label: t('status.signed')
        }, {
          key: 'job-done',
          label: t('status.jobDone')
        }] as const).map(filter => <button key={filter.key} onClick={() => setStatusFilter(filter.key)} className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors border ${statusFilter === filter.key ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200'}`} data-id="element-1501">
                {filter.label}
              </button>)}
          </div>

          <p className="text-xs text-gray-400 mb-2" data-id="element-1502">
            {visiblePTPs.filter(ptp => {
          if (statusFilter === 'all') return true;
          return ptp.status === statusFilter;
        }).length}{' '}
            {t('list.results')}
          </p>

          <div className="flex-1 overflow-y-auto pb-6 -mx-2 px-2" data-id="element-1503">
            {(() => {
          const filtered = visiblePTPs.filter(ptp => {
            if (statusFilter === 'all') return true;
            return ptp.status === statusFilter;
          });
          // Group by date label
          const groups: {
            label: string;
            items: typeof filtered;
          }[] = [];
          let lastLabel = '';
          filtered.forEach(ptp => {
            const label = getDateLabel(ptp.date);
            if (label !== lastLabel) {
              groups.push({
                label,
                items: [ptp]
              });
              lastLabel = label;
            } else {
              groups[groups.length - 1].items.push(ptp);
            }
          });
          let globalIdx = 0;
          return groups.map(group => <div key={group.label} className="mb-4" data-id="element-1504">
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 mt-1" data-id="element-1505">
                    {group.label === '@@today' ? t('date.today') : group.label === '@@yesterday' ? t('date.yesterday') : group.label}
                  </p>
                  <div className="space-y-2.5" data-id="element-1506">
                    {group.items.map(ptp => {
                const config = statusConfig[ptp.status];
                const StatusIcon = config.icon;
                const idx = globalIdx++;
                const isFirst = idx === 0 && showNewPTP;
                return <motion.button key={ptp.id} initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: idx * 0.08,
                  duration: 0.3
                }} onClick={() => handleCardPress(ptp)} className={`w-full text-left bg-white border rounded-xl p-4 transition-colors active:bg-gray-100 ${isFirst ? 'border-yellow-400/30 ring-1 ring-yellow-400/10' : 'border-gray-200'}`} data-id="element-1507">
                          <div className="flex items-start justify-between gap-3 mb-2" data-id="element-1508">
                            <div className="flex-1 min-w-0" data-id="element-1509">
                              <p className="text-gray-900 font-semibold text-sm leading-snug" data-id="element-1510">
                                {ptp.task}
                              </p>
                              <p className="text-gray-500 text-xs mt-0.5" data-id="element-1511">
                                {ptp.contractor}
                              </p>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0" data-id="element-1512">
                              <div className={`${config.bg} px-2 py-0.5 rounded-full flex items-center gap-1`} data-id="element-1513">
                                <StatusIcon className={`w-3 h-3 ${config.iconColor}`} data-id="element-1514" />
                                <span className={`text-[10px] font-bold uppercase tracking-wider ${config.text}`} data-id="element-1515">
                                  {config.label}
                                </span>
                              </div>
                              <ChevronRightIcon className="w-4 h-4 text-gray-400" data-id="element-1516" />
                            </div>
                          </div>
                          <div className="flex items-center gap-4 text-xs" data-id="element-1517">
                            <span className="text-gray-400" data-id="element-1518">{ptp.date}</span>
                            <span className="text-gray-300" data-id="element-1519">·</span>
                            <span className="text-gray-400" data-id="element-1520">
                              {ptp.crewSize} {t('list.workers')}
                            </span>
                            {ptp.meetingDone && ptp.score ? <>
                                <span className="text-gray-300" data-id="element-1521">·</span>
                                <span className="flex items-center gap-1" data-id="element-1522">
                                  {ptp.score < 80 && <AlertTriangleIcon className="w-3 h-3 text-amber-600" data-id="element-1523" />}
                                  <span className={ptp.score >= 80 ? 'text-green-600 font-medium' : 'text-amber-600 font-medium'} data-id="element-1524">
                                    {ptp.score}/100
                                  </span>
                                </span>
                              </> : !ptp.meetingDone && ptp.status !== 'draft' ? <>
                                <span className="text-gray-300" data-id="element-1525">·</span>
                                <span className="flex items-center gap-1 text-amber-600 font-medium" data-id="element-1526">
                                  <MicIcon className="w-3 h-3" data-id="element-1527" />
                                  {t('list.noRecording')}
                                </span>
                              </> : null}
                          </div>
                        </motion.button>;
              })}
                  </div>
                </div>);
        })()}
          </div>

          <button onClick={onNext} className="absolute bottom-4 right-4 z-20 w-14 h-14 rounded-full bg-yellow-400 text-gray-900 shadow-lg shadow-yellow-400/30 flex items-center justify-center active:scale-95 transition-transform" aria-label={t('list.createNew')} data-id="element-1528">
            <PlusIcon className="w-7 h-7" strokeWidth={2.5} data-id="element-1529" />
          </button>
        </>}
    </div>;
}